"use client";

import { useCallback, useMemo, useState } from "react";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
  type SensorDescriptor,
  type SensorOptions,
} from "@dnd-kit/core";
import {
  rectSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";

import { ProjectCard } from "./ProjectCard";
import { SortableProjectCard } from "./SortableProjectCard";
import type { Project } from "@/presentation/projects/types/Project";

export type ReorderableProjectGridProps = {
  projects: Project[];
  sensors: SensorDescriptor<SensorOptions>[];
  onDragEnd: (event: DragEndEvent, visibleIds: string[]) => void;
  onDragCancel: () => void;
};

/**
 * Sortable project grid with a portaled DragOverlay so backdrop-blur
 * can sample page content under the floating card.
 */
export function ReorderableProjectGrid({
  projects,
  sensors,
  onDragEnd,
  onDragCancel,
}: ReorderableProjectGridProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overlayWidth, setOverlayWidth] = useState<number | null>(null);

  const visibleIds = useMemo(
    () => projects.map((project) => project.id),
    [projects],
  );

  const activeProject = useMemo(
    () => projects.find((project) => project.id === activeId) ?? null,
    [activeId, projects],
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
    const width = event.active.rect.current.initial?.width;
    setOverlayWidth(typeof width === "number" ? width : null);
  }, []);

  const clearActive = useCallback(() => {
    setActiveId(null);
    setOverlayWidth(null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      onDragEnd(event, visibleIds);
      clearActive();
    },
    [clearActive, onDragEnd, visibleIds],
  );

  const handleDragCancel = useCallback(() => {
    onDragCancel();
    clearActive();
  }, [clearActive, onDragCancel]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={visibleIds} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {projects.map((project) => (
            <SortableProjectCard
              key={project.id}
              id={project.id}
              title={project.title}
              status={project.status}
              recordsCount={project.recordsCount}
              projectReportsCount={project.projectReportsCount}
              activeInterviewRoomsCount={
                project.activeInterviewRoomsCount ?? 0
              }
              projectId={project.id}
              ownerEmail={project.ownerEmail}
            />
          ))}
        </div>
      </SortableContext>

      <DragOverlay dropAnimation={null}>
        {activeProject ? (
          <div style={overlayWidth ? { width: overlayWidth } : undefined}>
            <ProjectCard
              title={activeProject.title}
              status={activeProject.status}
              recordsCount={activeProject.recordsCount}
              projectReportsCount={activeProject.projectReportsCount}
              activeInterviewRoomsCount={
                activeProject.activeInterviewRoomsCount ?? 0
              }
              ownerEmail={activeProject.ownerEmail}
              isDragOverlay
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
